
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock credentials for testing
const MOCK_USERS = [
  {
    id: '1',
    email: 'student@cmu.edu',
    password: 'student123',
    name: 'John Tartan',
    role: 'student'
  },
  {
    id: '2',
    email: 'admin@cmu.edu',
    password: 'admin123',
    name: 'Admin User',
    role: 'admin'
  },
  {
    id: '3',
    email: 'demo@cmu.edu',
    password: 'demo123',
    name: 'Demo Student',
    role: 'student'
  }
];

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is stored in localStorage or sessionStorage
    const storedUser = localStorage.getItem('campus_link_user') || sessionStorage.getItem('campus_link_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('campus_link_user');
        sessionStorage.removeItem('campus_link_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const matchedUser = MOCK_USERS.find(
      u => u.email === email && u.password === password
    );

    if (matchedUser) {
      const userData: User = {
        id: matchedUser.id,
        email: matchedUser.email,
        name: matchedUser.name,
        role: matchedUser.role
      };
      setUser(userData);
      // Store in both localStorage (persistent) and sessionStorage (session-based)
      localStorage.setItem('campus_link_user', JSON.stringify(userData));
      sessionStorage.setItem('campus_link_user', JSON.stringify(userData));
      return true;
    }

    return false;
  };

  const logout = () => {
    setUser(null);
    // Clear from both storage locations
    localStorage.removeItem('campus_link_user');
    sessionStorage.removeItem('campus_link_user');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
